import React, { useCallback, useRef, useState } from 'react';
import { Paperclip, FileAudio, Image as ImageIcon, AlertCircle, Download } from 'lucide-react';
import { chatAPI } from '../lib/api';

function statusLabel(status) {
  if (!status || status === 'done') return null;
  if (status === 'processing') return 'Procesando…';
  if (status === 'pending') return 'Pendiente';
  if (status === 'skipped_no_skill') return 'Sin skill en el agente';
  if (status === 'error') return 'Error';
  return status;
}

export function MessageAttachments({ conversationId, message, isUserBubble }) {
  const atts = message?.attachments || [];
  const blobCache = useRef({});
  const [, bump] = useState(0);

  const fetchBlob = useCallback(
    async (idx) => {
      const key = `${message.message_id}-${idx}`;
      if (blobCache.current[key]) return blobCache.current[key];
      const res = await chatAPI.downloadAttachment(conversationId, message.message_id, idx);
      const url = URL.createObjectURL(res.data);
      blobCache.current[key] = url;
      bump((n) => n + 1);
      return url;
    },
    [conversationId, message?.message_id]
  );

  if (!atts.length) return null;

  const muted = isUserBubble ? 'text-white/85' : 'text-zinc-600';

  return (
    <div className={`mt-2 space-y-2 text-xs ${muted}`}>
      {atts.map((a, idx) => {
        const st = statusLabel(a.status);
        return (
          <div
            key={`${message.message_id}-att-${idx}`}
            className={`rounded border p-2 ${
              isUserBubble ? 'border-white/25 bg-white/10' : 'border-brand-border bg-zinc-50/80'
            }`}
          >
            <div className="flex flex-wrap items-center gap-1 font-medium">
              {a.kind === 'audio' && <FileAudio className="w-3.5 h-3.5 shrink-0" />}
              {a.kind === 'image' && <ImageIcon className="w-3.5 h-3.5 shrink-0" />}
              {a.kind !== 'audio' && a.kind !== 'image' && <Paperclip className="w-3.5 h-3.5 shrink-0" />}
              <span className="truncate max-w-[200px]">{a.filename || a.kind || 'Adjunto'}</span>
              {st && <span className="opacity-75 font-normal">({st})</span>}
            </div>
            {a.transcript ? (
              <p className="mt-1.5 whitespace-pre-wrap leading-snug opacity-95">
                <span className="font-semibold">Transcripción: </span>
                {a.transcript}
              </p>
            ) : null}
            {a.text_extracted ? (
              <p className="mt-1.5 whitespace-pre-wrap leading-snug opacity-95">
                <span className="font-semibold">
                  {a.kind === 'document' ? 'Texto (documento): ' : 'Texto (OCR): '}
                </span>
                {a.text_extracted}
              </p>
            ) : null}
            {a.error ? (
              <p className="mt-1.5 text-red-600 flex items-start gap-1">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{a.error}</span>
              </p>
            ) : null}
            {a.storage_ref && a.kind === 'audio' ? (
              <AudioInline fetchBlob={() => fetchBlob(idx)} blobUrl={blobCache.current[`${message.message_id}-${idx}`]} />
            ) : null}
            {a.storage_ref && a.kind === 'image' ? (
              <button
                type="button"
                className={`mt-2 inline-flex items-center gap-1 text-[11px] underline ${
                  isUserBubble ? 'text-white' : 'text-brand-primary'
                }`}
                onClick={async () => {
                  const url = await fetchBlob(idx);
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                <Download className="w-3 h-3" />
                Ver imagen
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function AudioInline({ fetchBlob, blobUrl }) {
  const [src, setSrc] = useState(blobUrl || null);
  return (
    <div className="mt-2">
      {!src ? (
        <button type="button" className="text-[11px] underline opacity-90" onClick={() => fetchBlob().then(setSrc)}>
          Cargar reproductor de audio
        </button>
      ) : (
        <audio controls className="w-full max-w-xs h-8" src={src}>
          <track kind="captions" />
        </audio>
      )}
    </div>
  );
}
